import { Component, OnDestroy, signal } from '@angular/core';
import { DeliveryStatusService } from '../../services/delivery-status-service';
import { DbService } from '../../services/db.service';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '../../services/toast.service';
import {  Router } from '@angular/router';
import { IDeliveryStatus } from '../../interfaces/IDeliveryStatus';
import { CardModule } from 'primeng/card';
import { Button, ButtonModule } from 'primeng/button';
import { Toolbar } from 'primeng/toolbar';
import { DatePipe, formatDate, NgIf } from '@angular/common';
import { IShipment } from '../../interfaces/IShipment';
import { SplitButtonModule } from 'primeng/splitbutton';
import { IJustification } from '../../interfaces/IJustification';
import { MenuItem } from 'primeng/api';
import { ICategoryShipment } from '../../interfaces/ICategoryShipment';
import { ChipModule } from 'primeng/chip';
import { JustificationService } from '../../services/justification-service';
import { ICategoryShipmentDetail } from '../../interfaces/ICategoryShipmentDetail';
import { IPhotoEvidence } from '../../interfaces/IPhotoEvidence';
import { CarouselModule } from 'primeng/carousel';
import { WebcamInitError } from 'ngx-webcam';
import { IShipmentHistory } from '../../interfaces/IShipmentHistory';
import { FormsModule } from '@angular/forms';
import { CameraComponent } from '../../shared/camera/camera.component';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ShipmentService } from '../../services/shipment-service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {  ConfirmationService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IAwsPlace } from '../../interfaces/IAwsPlace';
import { AwsLocationService } from '../../services/aws-location.service';

@Component({
  selector: 'app-shipment-evidence',
  standalone: true,
  providers:[ConfirmationService],
  imports: [Toolbar, ButtonModule, CardModule, ChipModule, FormsModule, NgIf, Button, CarouselModule, SplitButtonModule, CameraComponent, SelectModule, TextareaModule,FloatLabelModule, ConfirmDialogModule, IconFieldModule, InputIconModule, AutoCompleteModule],   
  templateUrl: './delivery-evidence.component.html',
  styleUrl: './delivery-evidence.component.scss'
})
export class DeliveryEvidenceComponent implements OnDestroy {
  shipment:IShipment ={}as IShipment;
  categoryShipmentList:ICategoryShipment[] = [];
  deliveryStatusList:IDeliveryStatus[]=[];
  selectedStatus:IDeliveryStatus|null = null;
  justifications:MenuItem[] = [];
  justificationData:IJustification[] = [];
  PICTURES_MAX_LENGTH = 10;
  PICTURES_MAX_WIDTH = 1024;
  PICTURES_QUALITY = 0.7;
  VALID_PHOTO_EXT:string[] =["png", "jpg", "jpeg"];
  isCameraOpen = false;
  currentCategoryShipment: ICategoryShipment | null = null; 
  comments:string="";
  NOT_SHOW_STATUSES = ["CREATED", "CANCELED"];
  addressList: IAwsPlace[] = [];
  selectedAddress: IAwsPlace | string | undefined;
  isOnline = signal(navigator.onLine);
  private onlineHandler = () => this.isOnline.set(true);
  private offlineHandler = () => this.isOnline.set(false);
  private searchSubject = new Subject<string>();
  private searchSubscription = new Subscription();

  constructor(private deliveryStatusService:DeliveryStatusService, private dbService:DbService,
              private toast:ToastService, private router:Router, private justificationService:JustificationService,
              private shipmentService:ShipmentService, private confirmationService:ConfirmationService,
              private authService:AuthService, private locationService: AwsLocationService) {
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      if (!query) {
        this.addressList = [];
        return;
      }

      this.locationService.searchPlaceIndexForText(query)
        .then((suggestions) => {
          this.addressList = suggestions;
        })
        .catch(() => {
          this.addressList = [];
        });
    });

    const state = this.router.getCurrentNavigation()?.extras.state;
    this.shipment = state?.['data'];
    this.categoryShipmentList = this.shipment.categoryShipment;
    this.categoryShipmentList.map(cs => cs.categoryShipmentDetail = [{
      categoryShipmentId:cs.id,
      photoEvidences:[] as IPhotoEvidence[]
    } as ICategoryShipmentDetail]);
  }

  async ngOnInit() {
    if(!navigator.onLine){
      await this.validateDeliveryStatus();
      await this.validateJustification();
      return;
    }

    try{
      let result = await firstValueFrom(this.justificationService.getActive());
      if(result.data){
        await this.dbService.saveJustificationList(result.data);
        this.justificationData = result.data;
        this.justifications = result.data.map(j => {return {label:j.name} });
      }else{
        this.validateJustification();
      }
    }catch{
      this.validateJustification();
    }

    try{
      let result = await firstValueFrom(this.deliveryStatusService.getActive());
      if(result.data){
        await this.dbService.saveDeliveryStatusList(result.data);
        this.deliveryStatusList = result.data.filter(ds => !this.NOT_SHOW_STATUSES.includes(ds.status));
      }else{
        this.validateDeliveryStatus();
      }
    }catch{
      this.validateDeliveryStatus();
    }
  }

  search(event: AutoCompleteCompleteEvent) {
    if (!this.isOnline()) {
      this.addressList = [];
      return;
    }
    this.searchSubject.next(event.query?.trim() ?? '');
  }

  async validateDeliveryStatus(){
    let statusList = await this.dbService.getDeliveryStatusList();
    if(!statusList || statusList.length <= 0){
      this.toast.error("No Update Status Found", "Please connect to the internet to obtain the status list", 5000);
      this.goBack();
      return;
    }
    this.deliveryStatusList=statusList.filter(ds => !this.NOT_SHOW_STATUSES.includes(ds.status));
  }

  async validateJustification(){
    let justifications = await this.dbService.getJustificationList();
    if(!justifications || justifications.length <= 0){
      this.toast.error("No Justifications Found", "Please connect to the internet to obtain the justification list", 5000);
      this.goBack();
      return;
    }
    this.justificationData = justifications;
    this.justifications = justifications.map(j => {return {label:j.name} });
  }

  goBack(){
    this.router.navigate(['/detail'], { state: { data: this.shipment?.id } });
  }

  openCamera(categoryShipment:ICategoryShipment){
    this.currentCategoryShipment = categoryShipment;
    this.isCameraOpen = true;
  }

  closeCamera(){
    this.isCameraOpen = false;
    this.currentCategoryShipment = null;
  }


  onCameraInitError(error: WebcamInitError){
    this.toast.error('Camera initialization error: ' + error.message);
    this.closeCamera();
  }

  removeJustification(categoryShipment:ICategoryShipment){
    categoryShipment.categoryShipmentDetail[0].justification = undefined;
    categoryShipment.categoryShipmentDetail[0].justificationId = null;
  }

  addJustification(categoryShipment:ICategoryShipment, justificationName:string){
    let justification = this.justificationData.find(j => j.name == justificationName);
    categoryShipment.categoryShipmentDetail[0].justification = justification;
    if (justification) {
      categoryShipment.categoryShipmentDetail[0].justificationId = justification.id;
    }
    categoryShipment.categoryShipmentDetail[0].photoEvidences = [];

  }

  updateJustificationCommand(categoryShipment:ICategoryShipment){
    this.justifications.map(j => j.command = () => this.addJustification(categoryShipment, j.label||""));
  }

  getAllPhotos(){
    return this.categoryShipmentList.flatMap(cs => cs.categoryShipmentDetail).filter(cs =>cs.photoEvidences != null).flatMap(d => d.photoEvidences);
  }

  onFileSelected(event: any, categoryShipment:ICategoryShipment): void {
    const files =  event.target.files;
    let categoryShimpentDetail = categoryShipment.categoryShipmentDetail[0];
    if (files && files.length > 0) {

      const availableSlots = this.PICTURES_MAX_LENGTH - (categoryShimpentDetail.photoEvidences? categoryShimpentDetail.photoEvidences.length : 0 );
      if (files.length > availableSlots) {
        this.toast.warn("The number of photos must be less than 10");
        return;
      }

      for (const file of files) {
        const name = (file.name || '').trim();
        let allPhotos = this.getAllPhotos();
        const duplicate = allPhotos.some(n => n.name.toLowerCase() === name.toLowerCase());
        if (duplicate) {
          this.toast.warn("A photo with the same name already exists.");
          continue;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
          await this.addPicture( e.target?.result as string, categoryShimpentDetail, name);
        };
        reader.readAsDataURL(file);        
      }
      event.target.value = ''; 
    }
  }

  async addPicture(dataUrl:string, categoryShimpentDetail:ICategoryShipmentDetail, name:string){                         
    const contentType = dataUrl.match(/^data:(.*?);/)?.[1] || '';
    const fileExt = name.split('.').pop()?.toLowerCase() || '';
    if(!this.VALID_PHOTO_EXT.includes(fileExt.toLowerCase())){
      
      this.toast.warn(`\"${fileExt}\" is not a valid file extension, the valid ones are: ${this.VALID_PHOTO_EXT.join(', ')}`);
      return;
    }
    await this.compressImage(dataUrl, contentType, 1024, 0.7).then(result => {
      dataUrl = result;
    });  
    
    categoryShimpentDetail.photoEvidences.unshift({name: name, base64Photo: dataUrl, fileContentType: contentType, fileExt: fileExt} as IPhotoEvidence);
    categoryShimpentDetail.photoEvidences=[...categoryShimpentDetail.photoEvidences];
  }

  compressImage(base64: string, contentType: string, maxWidth: number, quality: number): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = height * (maxWidth / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        const compressed = canvas.toDataURL(contentType, quality);

        resolve(compressed);
      };
    });
  }

  removePhoto(categoryShimpentDetail:ICategoryShipmentDetail, photo: IPhotoEvidence): void {
    const index = categoryShimpentDetail.photoEvidences.indexOf(photo);
    categoryShimpentDetail.photoEvidences.splice(index, 1);
  }

  async savePhoto(photos:string[], categoryShimpentDetail:ICategoryShipmentDetail){
    let match;
    for (const photo of photos) {
      match = photo.match(/data:image\/(.*?);base64/) || "";
      await this.addPicture(photo,categoryShimpentDetail , `image.${match[1]}` );
    }
  }

  handleInitError(error: WebcamInitError): void {
      this.toast.warn(`Unable to access to the camera. Error: ${error.message}`);
      this.closeCamera();
  }


  save(){
    if(!this.selectedStatus){
      this.toast.warn("Please select a status");
      return;
    }

    const addressLabel = this.selectedAddress ? (typeof this.selectedAddress === 'string'
          ? this.selectedAddress.trim() : (this.selectedAddress as IAwsPlace).Address.Label)
          : '';

    if(this.selectedStatus.isLastStatus){
      if( !this.categoryShipmentList.every(cs => cs.categoryShipmentDetail.every(csd => csd.justification || csd.photoEvidences.length > 0))){
        this.toast.warn("Missing information", "Please upload evidence for each category or select a justification");
        return;
      }
    }else{
      if(!addressLabel && !this.comments){
        this.toast.warn("Missing information", "Please provide a comment or an address");
        return;
      }
    } 

    let categoryShipmentDetails: ICategoryShipmentDetail[]|null = null;
    if( this.categoryShipmentList.some(cs => cs.categoryShipmentDetail.some(csd => csd.justification || csd.photoEvidences.length > 0))){
        categoryShipmentDetails = structuredClone(this.categoryShipmentList.flatMap(cs => cs.categoryShipmentDetail));
      }

    let history: IShipmentHistory = {
          shipmentId: this.shipment.id,
          deliveryStatus:this.selectedStatus,
          deliveryStatusId:this.selectedStatus.id,
          updatedBy: this.authService.getUsername(),
          updatedDate: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'", 'en-US', 'UTC'),
          categoryShipmentDetail: categoryShipmentDetails,
          comments:this.comments,
          address: addressLabel ? { label: addressLabel } : null
        } as IShipmentHistory;


    history.categoryShipmentDetail?.map(csd => csd.photoEvidences.map(p => p.base64Photo = p.base64Photo.split(",")[1]))
    this.shipmentService.saveShipmentHistory(history).subscribe(async result => {
      if(result.success){
        this.selectedAddress = undefined;
        this.toast.success("The evidence was uploaded successfully");
        await  this.updateHistoryLocal(history);
        this.goBack();
      }
    }, err=>{
      if(err.status === 0){
        this.dbService.queueShipmentUpdate(history).then(async ()=>{
          this.selectedAddress = undefined;
          this.toast.info("There is no internet connection; the evidence will be uploaded automatically when it is restored.");
          await  this.updateHistoryLocal(history);
          this.goBack();
        });
      }
    });
  }


  async updateHistoryLocal(history: IShipmentHistory){
    
    if(history.deliveryStatus.isLastStatus){
      await this.dbService.deleteShipmentById(this.shipment.id).then(()=>{
        this.toast.success('Shipment Completed!');
      });

      if(!navigator.onLine){
        return;
      }
      const remaining = await this.dbService.getShipments();
      if(remaining.length === 0){
        this.authService.logout();
        return;
      }

    }else{
      this.shipment.deliveryStatus = history.deliveryStatus;
      this.shipment.shipmentHistories = [...this.shipment.shipmentHistories, history];    
        await this.dbService.updateShipment(this.shipment).catch(error => {
          this.toast.error('Failed to update shipment locally', 'An error occurred while updating the shipment history in local storage. Please try again.');
      });
    }
    
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
  }


   statusChange(event:SelectChangeEvent) {

      if(this.selectedStatus?.isLastStatus){

        this.confirmationService.confirm({
            target: event.originalEvent.target as EventTarget,
            message: "If you select this status the shipment will be closed and you couldn't upload more evidence. Do you want to continue?",
            header: `'${this.selectedStatus.status}' is the last status of this shipment`,
            icon: 'pi pi-info-circle',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                label: 'Cancel',
                severity: 'secondary',
                outlined: true
            },
            acceptButtonProps: {
                label: 'Confirm',
                outlined: true
            },      
            reject: () => {
                this.selectedStatus = null;
            } 
        });
      }

    }

}
